import { useSelector } from 'react-redux';
import { IS_SELF_SIGNUP_ENABLED } from 'src/config';
import { BillingPlanType } from 'src/content/Settings/types';
import { SubscriptionInfo } from 'src/redux/slices/library';
import { RootState } from 'src/redux/store';
import {
  ACCOUNT_TYPE,
  SUBSCRIPTION_CODE,
  SUBSCRIPTION_INTERVAL,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_STATUS
} from 'src/types/enum';

const getSubscriptionName = (subscriptionInfo: SubscriptionInfo) => {
  if (subscriptionInfo.tier_code === SUBSCRIPTION_CODE.TRIAL) {
    return 'Trial';
  }
  return subscriptionInfo.tier_name;
};
const useSubscriptionPlan = () => {
  const { subscriptionInfo } = useSelector((state: RootState) => state.library);
  const accountType = useSelector(
    (state: RootState) => state.auth.user?.accountType
  );

  const subscriptionTierCode = subscriptionInfo?.tier_code;
  const subscriptionBillingPeriod = subscriptionInfo?.billing_period;
  const subscriptionStatus = subscriptionInfo?.status;
  const subscriptionTierName = getSubscriptionName(subscriptionInfo);
  const subscriptionExpiryOn = subscriptionInfo?.expiry_on;
  const trialDaysRemaining = subscriptionInfo?.remaining_days;
  const isCancelAtPeriodEnd = Boolean(subscriptionInfo?.cancel_at_period_end);
  const isTrialPlan = subscriptionTierCode === SUBSCRIPTION_CODE.TRIAL;
  const isEssentialPlan = subscriptionTierCode === SUBSCRIPTION_CODE.ESSENTIALS;
  const isProfessionalPlan =
    subscriptionTierCode === SUBSCRIPTION_CODE.PROFESSIONAL;
  const isStandardPlan = subscriptionTierCode === SUBSCRIPTION_CODE.STANDARD;
  const isSubscriptionStatusActive =
    subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE;
  const isSubscriptionStatusPastDue =
    subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE;
  const isSubscriptionStatusCancelled =
    subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED;
  const isSubscriptionStatusBlocked =
    subscriptionStatus === SUBSCRIPTION_STATUS.BLOCKED;
  const isSubscriptionStatusExpired =
    subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED;
  const isSubscriptionStatusRenewed =
    subscriptionStatus === SUBSCRIPTION_STATUS.RENEWED;

  const hasAnySubscription =
    isTrialPlan || isEssentialPlan || isProfessionalPlan;
  const hasBasicSubscription = isTrialPlan || isEssentialPlan;
  const hasProfessionalSubscription = isProfessionalPlan;

  const isYearlyPlan =
    subscriptionBillingPeriod === SUBSCRIPTION_INTERVAL.YEARLY;
  const isMonthlyPlan =
    subscriptionBillingPeriod === SUBSCRIPTION_INTERVAL.MONTHLY;

  const isEssentialMonthlyPlan =
    isEssentialPlan &&
    isMonthlyPlan &&
    isSubscriptionStatusActive &&
    !subscriptionInfo?.cancel_at_period_end;
  const isProfessionalMonthlyPlan =
    isProfessionalPlan &&
    isMonthlyPlan &&
    isSubscriptionStatusActive &&
    !subscriptionInfo?.cancel_at_period_end;

  const isEssentialYearlyPlan =
    isEssentialPlan &&
    isYearlyPlan &&
    isSubscriptionStatusActive &&
    !subscriptionInfo?.cancel_at_period_end;

  const isProfessionalYearlyPlan =
    isProfessionalPlan &&
    isYearlyPlan &&
    isSubscriptionStatusActive &&
    !subscriptionInfo?.cancel_at_period_end;

  const isSubscriptionExpired =
    subscriptionInfo?.is_expired ||
    isSubscriptionStatusExpired ||
    (isSubscriptionStatusCancelled && isTrialPlan);

  const isCardLinked = subscriptionInfo?.is_card_linked;

  const isEssentialPlanActive = isEssentialPlan && isSubscriptionStatusActive;
  const isProfessionalPlanActive =
    isProfessionalPlan && isSubscriptionStatusActive;

  const isPaygLicenseApplicable =
    IS_SELF_SIGNUP_ENABLED &&
    !isTrialPlan &&
    (isEssentialPlan || isProfessionalPlan);

  const canAccessPayg =
    isPaygLicenseApplicable && accountType === ACCOUNT_TYPE.ORGANIZATION_OWNER;

  const subscriptionExpiredAllowedPath = '/settings/billing';
  const disableMonthlyInCheckout = (
    selectedPlan: SUBSCRIPTION_PLAN | undefined
  ): boolean => {
    if (!isEssentialPlanActive || !selectedPlan) return false;
    if (selectedPlan === SUBSCRIPTION_PLAN.ESSENTIALS) return true;
    if (
      selectedPlan === SUBSCRIPTION_PLAN.PROFESSIONAL &&
      isEssentialYearlyPlan
    ) {
      return true;
    }
    return false;
  };

  const isCurrentPlan = (
    plan: BillingPlanType,
    billingPeriod: SUBSCRIPTION_INTERVAL
  ): boolean => {
    return (
      subscriptionInfo?.tier_code === plan?.code &&
      subscriptionInfo?.billing_period === billingPeriod
    );
  };

  return {
    subscriptionInfo,
    isPaygLicenseApplicable,
    canAccessPayg,
    isTrialPlan,
    isEssentialPlan,
    isProfessionalPlan,
    isStandardPlan,
    isSubscriptionStatusActive,
    isSubscriptionStatusPastDue,
    isSubscriptionStatusCancelled,
    isSubscriptionStatusBlocked,
    isSubscriptionStatusExpired,
    isSubscriptionStatusRenewed,
    hasAnySubscription,
    hasBasicSubscription,
    hasProfessionalSubscription,
    isYearlyPlan,
    isMonthlyPlan,
    subscriptionTierCode,
    subscriptionBillingPeriod,
    subscriptionStatus,
    subscriptionTierName,
    subscriptionExpiryOn,
    isProfessionalMonthlyPlan,
    isEssentialMonthlyPlan,
    isSubscriptionExpired,
    isCardLinked,
    isEssentialYearlyPlan,
    isProfessionalYearlyPlan,
    trialDaysRemaining,
    isCancelAtPeriodEnd,
    isEssentialPlanActive,
    isProfessionalPlanActive,
    subscriptionExpiredAllowedPath,
    disableMonthlyInCheckout,
    isCurrentPlan
  };
};

export default useSubscriptionPlan;
